import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { PlaceholderMedia } from '../../primitives/PlaceholderMedia.js';
import { ALIGN_OPTIONS, marginFields, visibilityField } from '../_shared/blockFields.js';
import { marginStyle, num, str, visibilityCss } from '../_shared/blockRender.js';
/**
 * Shrine `image` (generic content block) — config-by-config. A STANDALONE image (its OWN `image_picker`
 * setting, NOT the product gallery) that the merchant drops anywhere. Shrine's settings, same ids/order:
 *  - `visibility`     — Display on (All devices / Mobile only / Desktop only).
 *  - `image`          — the uploaded image (image_picker).
 *  - `width`          — % of the container width (range 0–100, step 10, def 100).
 *  - `alignment`      — desktop horizontal alignment (Left/Center/Right; Shrine stores flex-start/center/
 *                       flex-end, applied as the flex `justify-content` of the wrapper, def Center).
 *  - `mobileAlignment`— mobile horizontal alignment (same options, def Center) — responsive at 750px.
 *  - `borderRadius`   — corner radius (range 0–40px, step 2, def 0).
 *  - Margins          — top/bottom (def 15/15).
 *
 * Aspect ratio / fit: Shrine renders the image at its INTRINSIC aspect ratio inside a `.ratio` frame
 * (`--ratio-percent` = 1/aspect_ratio). We reproduce that with the image's own width/height (object-fit
 * cover within an aspect-ratio box when both dims are known) and fall back to `auto` height otherwise so
 * the natural ratio is preserved. When NO image is set we render a neutral, selectable placeholder frame
 * (a 4:3 box with a small image glyph) instead of returning null, so the block stays visible + clickable
 * in the builder (matches Shrine's `placeholder_svg_tag`).
 *
 * DEFERRED (Shopify-only / out of scope, kept self-contained): responsive `image_url`/`srcset` width
 * generation + Shopify CDN sizing, and reveal-on-scroll animation. The src is used as-is from the
 * uploaded ImageValue.
 */
export const imageBlockSchema = {
    type: 'image',
    name: 'Image',
    fields: [
        visibilityField,
        { id: 'image', type: 'image', label: 'Image' },
        {
            id: 'width',
            type: 'range',
            label: 'Width',
            min: 5,
            max: 100,
            step: 5,
            default: 100,
            unit: '%',
        },
        {
            id: 'imageMaxWidth',
            type: 'range',
            label: 'Max width',
            info: 'Caps the image in pixels whatever the section width — for a badge or a logo. 0 = no cap.',
            min: 0,
            max: 800,
            step: 10,
            default: 0,
            unit: 'px',
        },
        {
            id: 'alignment',
            type: 'select',
            label: 'Alignment',
            default: 'center',
            options: ALIGN_OPTIONS,
        },
        {
            id: 'mobileAlignment',
            type: 'select',
            label: 'Mobile alignment',
            default: 'center',
            options: ALIGN_OPTIONS,
        },
        {
            id: 'borderRadius',
            type: 'range',
            label: 'Corner radius',
            min: 0,
            max: 40,
            step: 2,
            default: 0,
            unit: 'px',
        },
        ...marginFields(15, 15),
    ],
};
/** Map our left/center/right (and Shrine's raw flex-start/flex-end) to a CSS `justify-content` value. */
function justify(value) {
    switch (value) {
        case 'left':
        case 'flex-start':
            return 'flex-start';
        case 'right':
        case 'flex-end':
            return 'flex-end';
        case 'center':
        default:
            return 'center';
    }
}
/** Read the block's ImageValue if a real uploaded image is set (must have a non-empty url). */
function imageValue(v) {
    if (v && typeof v === 'object' && typeof v.url === 'string') {
        const iv = v;
        if (iv.url.trim() !== '')
            return iv;
    }
    return undefined;
}
/**
 * Standalone image block. Honors width (% of container), desktop/mobile alignment (responsive at 750px
 * via BOTH `@media` and `@container aqx-page`), and corner radius. The image keeps its intrinsic aspect
 * ratio; when unset, a neutral placeholder frame renders so the block stays visible + selectable.
 */
const ImageBlock = ({ block }) => {
    const bs = block.settings;
    const cls = `aqx-im-${block.id}`;
    const img = imageValue(bs.image);
    const width = num(bs.width, 100); // % of container
    const maxPx = num(bs.imageMaxWidth, 0); // px cap (0 = none) — a 90px badge stays 90px in a 1400px section
    const radius = num(bs.borderRadius, 0); // px
    const deskJustify = justify(bs.alignment);
    const mobJustify = justify(bs.mobileAlignment);
    // Wrapper aligns the (possibly narrower-than-container) image horizontally. Mobile-first base uses the
    // mobile alignment; desktop overrides ≥750px — mirrored in @media (live) + @container (builder preview).
    const mobileRule = `.${cls}{justify-content:${mobJustify}}`;
    const css = `.${cls}{justify-content:${deskJustify}}` +
        `@media(max-width:749px){${mobileRule}}` +
        `@container aqx-page (max-width:749px){${mobileRule}}` +
        visibilityCss(cls, bs.visibility);
    const wrapperStyle = {
        display: 'flex',
        inlineSize: '100%',
        ...marginStyle(bs, 15, 15),
    };
    // The framed image/placeholder takes `width%` of the container; the flex wrapper positions it.
    const frameStyle = {
        inlineSize: `${width}%`,
        maxInlineSize: maxPx > 0 ? `min(100%, ${maxPx}px)` : '100%',
        borderRadius: radius > 0 ? `${radius}px` : 'var(--aqx-theme-radius-media, 0px)',
        overflow: 'hidden',
    };
    // Honor the image's intrinsic aspect ratio (CLS-safe) when both dims are known; otherwise let height be
    // auto so the natural ratio is preserved. object-fit: cover when constrained by a known ratio.
    const hasRatio = img != null &&
        typeof img.width === 'number' &&
        typeof img.height === 'number' &&
        img.width > 0 &&
        img.height > 0;
    const imgStyle = {
        display: 'block',
        inlineSize: '100%',
        blockSize: hasRatio ? '100%' : 'auto',
        objectFit: hasRatio ? 'cover' : undefined,
    };
    return (_jsxs("div", { className: cls, style: wrapperStyle, children: [_jsx("style", { dangerouslySetInnerHTML: { __html: css } }), _jsx("div", { style: hasRatio ? { ...frameStyle, aspectRatio: `${img.width} / ${img.height}` } : frameStyle, children: img ? (_jsx("img", { src: img.url, alt: str(img.alt), width: img.width, height: img.height, loading: "lazy", style: imgStyle })) : (
                // Neutral scenic placeholder — aquilixShop's answer to Shrine `placeholder_svg_tag`. Keeps the
                // empty block visible + selectable in the builder and re-tints to the active scheme.
                _jsx(PlaceholderMedia, { style: { aspectRatio: '4 / 3', blockSize: 'auto' } })) })] }));
};
export const imageBlock = {
    type: 'image',
    schema: imageBlockSchema,
    Component: ImageBlock,
    meta: { label: 'Image' },
};
//# sourceMappingURL=index.js.map